<div class="amortization-container">
  <mat-toolbar color="primary">
    <button mat-icon-button (click)="goBack()">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <span>Back to Banks</span>
  </mat-toolbar>

  @if (selectedLoan()) {
    <div class="loan-details">
      <mat-card>
        <mat-card-content>
          <h2>{{ loanName() }}</h2>
          <div class="loan-summary-inline">
            <div class="summary-item-inline">
              <span class="label">Contact</span>
              <span class="value">{{ contact()?.first_name }} {{ contact()?.last_name }}</span>
            </div>
            <div class="summary-item-inline">
              <span class="label">Loan Amount</span>
              <span class="value">{{ loanAmount() | currency:'USD':'symbol':'1.2-2' }}</span>
            </div>
            <div class="summary-item-inline">
              <span class="label">Interest Rate</span>
              <span class="value">{{ loanInterestRate() }}%</span>
            </div>
            <div class="summary-item-inline">
              <span class="label">Term</span>
              <span class="value">{{ loanTerm() }} {{ loanFrequency() === 'monthly' ? 'months' : 
                                    loanFrequency() === 'bi-weekly' ? 'bi-weekly' : 
                                    loanFrequency() === 'weekly' ? 'weekly' : '' }}</span>
            </div>
            <div class="summary-item-inline">
              <span class="label">Payment</span>
              <span class="value">{{ loanPayment() | currency:'USD':'symbol':'1.2-2' }}</span>
            </div>
            <div class="summary-item-inline">
              <span class="label">Close Date</span>
              <span class="value">{{ loanCloseDate() | date:'MM/dd/yyyy' }}</span>
            </div>
          </div>

          <div class="loan-totals">
            <div class="summary-item">
              <span class="label">Total Interest</span>
              <span class="value">{{ getTotalInterest(selectedLoan()) | currency:'USD':'symbol':'1.2-2' }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Total Paid</span>
              <span class="value">{{ getTotalPaid(selectedLoan()) | currency:'USD':'symbol':'1.2-2' }}</span>
            </div>
            @if(loanRemainingBalance() != null) {
              <div class="summary-item">
                <span class="label">Remaining Balance</span>
                <span class="value">{{ loanRemainingBalance() | currency:'USD':'symbol':'1.2-2' }}</span>
              </div>
            }
            @if(loanNextPaymentDate()) {
              <div class="summary-item">
                <span class="label">Next Payment Date</span>
              <span class="value">{{ loanNextPaymentDate() | date:'MM/dd/yyyy' }}</span>
            </div>
            }
            <div class="summary-item make-payment-item">
              <button mat-raised-button color="accent" (click)="openPaymentDialog()" class="make-payment-button">
                <mat-icon>payment</mat-icon>
                Make Payment
              </button>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>

    <div class="amortization-table-container">
      <mat-card>
        <mat-card-content>
          <h2>Amortization Schedule</h2>
          
          <table mat-table [dataSource]="amortizationSchedule()" class="amortization-table">
            <!-- Payment Number Column -->
            <ng-container matColumnDef="paymentNumber">
              <th mat-header-cell *matHeaderCellDef>Payment #</th>
              <td mat-cell *matCellDef="let payment">{{ payment.paymentNumber | number:'1.0-0' }}</td>
            </ng-container>

            <!-- Payment Date Column -->
            <ng-container matColumnDef="paymentDate">
              <th mat-header-cell *matHeaderCellDef>Payment Date</th>
              <td mat-cell *matCellDef="let payment">
                {{ payment.actualDate || payment.paymentDate | date:'MM/dd/yyyy' }}
                @if (payment.actualDate && payment.paymentDate && payment.actualDate.getTime() !== payment.paymentDate.getTime()) {
                  <span class="scheduled-date">(Scheduled: {{ payment.paymentDate | date:'MM/dd/yyyy' }})</span>
                }
              </td>
            </ng-container>

            <!-- Payment Column -->
            <ng-container matColumnDef="payment">
              <th mat-header-cell *matHeaderCellDef>Payment</th>
              <td mat-cell *matCellDef="let payment" [class.actual-payment]="payment.isActual ? true : false">
                {{ payment.payment | currency:'USD':'symbol':'1.2-2' }}
              </td>
            </ng-container>

            <!-- Principal Column -->
            <ng-container matColumnDef="principal">
              <th mat-header-cell *matHeaderCellDef>Principal</th>
              <td mat-cell *matCellDef="let payment" [class.actual-payment]="payment.isActual ? true : false">
                {{ payment.state === 'Funding' ? '' : (payment.principal | currency:'USD':'symbol':'1.2-2') }}
              </td>
            </ng-container>

            <!-- Interest Column -->
            <ng-container matColumnDef="interest">
              <th mat-header-cell *matHeaderCellDef>Interest</th>
              <td mat-cell *matCellDef="let payment" [class.actual-payment]="payment.isActual ? true : false">
                {{ payment.state === 'Funding' ? '' : (payment.interest | currency:'USD':'symbol':'1.2-2') }}
              </td>
            </ng-container>

            <!-- Late Fee Column -->
            <ng-container matColumnDef="lateFee">
              <th mat-header-cell *matHeaderCellDef>Late Fee</th>
              <td mat-cell *matCellDef="let payment" [class.actual-payment]="payment.isActual ? true : false">
                {{ payment.state === 'Funding' ? '' : (payment.lateFee ? (payment.lateFee | currency:'USD':'symbol':'1.2-2') : '-') }}
              </td>
            </ng-container>

            <!-- Balance Column -->
            <ng-container matColumnDef="balance">
              <th mat-header-cell *matHeaderCellDef>Balance</th>
              <td mat-cell *matCellDef="let payment">
                {{ payment.balance | currency:'USD':'symbol':'1.2-2' }}
              </td>
            </ng-container>

            <!-- State Column -->
            <ng-container matColumnDef="state">
              <th mat-header-cell *matHeaderCellDef>State</th>
              <td mat-cell *matCellDef="let payment" [class.paid-state]="payment.state === 'Paid'" 
                                                     [class.scheduled-state]="payment.state === 'Scheduled'"
                                                     [class.funding-state]="payment.state === 'Funding'"
                                                     [class.extra-payment-state]="payment.state === 'Extra Payment'">
                {{ payment.state }}
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;" 
                [class.actual-row]="row.isActual ? true : false"></tr>
          </table>
        </mat-card-content>
      </mat-card>
    </div>
  } @else {
    <div class="loading-container">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Loading loan information...</p>
    </div>
  }
</div>
